import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import AuthGuard from './components/AuthGuard';
import { getAuth, getDashboardUrl } from './utils/auth';
import { PermissionProvider } from './contexts/PermissionContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Layouts
import AuthLayout from './layouts/AuthLayout';
import CustomerLayout from './layouts/CustomerLayout';
import AdminLayout from './layouts/AdminLayout';

// Landing Page
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Auth Pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AuthError from './pages/auth/AuthError';

// Customer Pages
import Dashboard from './pages/customer/Dashboard';
import Inventory from './pages/customer/Inventory';
import RequestsList from './pages/customer/RequestsList';
import RequestDetail from './pages/customer/RequestDetail';
import CreateRequest from './pages/customer/CreateRequest';
import Invoices from './pages/customer/Invoices';
import CustomerProfile from './pages/customer/CustomerProfile';
import CustomerUserSettings from './pages/customer/UserSettings';
import CustomerStorageFees from './pages/customer/StorageFees';
import InvoiceDetail from './pages/customer/InvoiceDetail';
import ShippingRates from './pages/customer/ShippingRates';
import ShippingReview from './pages/customer/ShippingReview';
import ShippingOptions from './pages/customer/ShippingOptions';
import UserManagement from './pages/customer/UserManagement';
import CustomerAddresses from './pages/customer/CustomerAddresses';
import InboundProjects from './pages/customer/InboundProjects';
import CreateInboundProject from './pages/customer/CreateInboundProject';
import InboundProjectDetail from './pages/customer/InboundProjectDetail';
import CustomerInventoryDetail from './pages/customer/InventoryDetail';
import CustomerInventoryEdit from './pages/customer/InventoryEdit';
import CustomerFeedback from './pages/customer/Feedback';
import ChangeLog from './pages/ChangeLog';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import InventoryList from './pages/admin/inventory/InventoryList';
import AdminInventoryDetail from './pages/admin/inventory/InventoryDetail';
import InventoryForm from './pages/admin/inventory/InventoryForm';
import CustomersList from './pages/admin/customers/CustomersList';
import AdminRequestsList from './pages/admin/requests/RequestsList';
import AdminRequestDetail from './pages/admin/requests/RequestDetail';
import AdminCreateRequest from './pages/admin/requests/CreateRequest';
import ReceivingScreen from './pages/admin/receiving/ReceivingScreen';
import InvoicesList from './pages/admin/invoices/InvoicesList';
import AdminInvoiceDetail from './pages/admin/invoices/InvoiceDetail';
import UsersList from './pages/admin/users/UsersList';
import Settings from './pages/admin/settings/Settings';
import CustomerForm from './pages/admin/customers/CustomerForm';
import CustomerDetail from './pages/admin/customers/CustomerDetail';
import UserView from './pages/admin/users/UserView';
import UserEdit from './pages/admin/users/UserEdit';
import AdminShippingOptions from './pages/admin/requests/ShippingOptions';
import AuditLogs from './pages/admin/AuditLogs';
import CorrectionsList from './pages/admin/corrections/CorrectionsList';
import CorrectionDetail from './pages/admin/corrections/CorrectionDetail';
import CreateAdjustment from './pages/admin/corrections/CreateAdjustment';
import CreateTransfer from './pages/admin/corrections/CreateTransfer';
import ZohoWebhookEvents from './pages/admin/ZohoWebhookEvents';
import BillingExceptions from './pages/admin/billing/BillingExceptions';
import StorageUnitsList from './pages/admin/storageUnits/StorageUnitsList';
import AdminCreateInboundProject from './pages/admin/inboundProjects/CreateInboundProject';
import ManifestReceivingScreen from './pages/admin/manifests/ManifestReceivingScreen';
import AdminHelp from './pages/admin/Help';


// Create a QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5 // 5 minutes
    }
  }
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
        <NotificationProvider>
          <PermissionProvider>
            <Router>
            <Routes>
              {/* Landing page for unauthenticated users, dashboard redirect for authenticated */}
              <Route path="/" element={<RootRedirect />} />
              
              {/* Legal pages */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />

              {/* Auth routes */}
              <Route path="/login" element={<AuthLayout />}>
                <Route index element={<Login />} />
              </Route>
              <Route path="/forgot-password" element={<AuthLayout />}>
                <Route index element={<ForgotPassword />} />
              </Route>
              <Route path="/reset-password/:token" element={<AuthLayout />}>
                <Route index element={<ResetPassword />} />
              </Route>
              <Route path="/auth-error" element={<AuthLayout />}>
                <Route index element={<AuthError />} />
              </Route>

              {/* Customer routes */}
              <Route path="/customer" element={
                <AuthGuard requiredRole="customer">
                  <CustomerLayout />
                </AuthGuard>
              }>
                <Route index element={<Dashboard />} />
                <Route path="inventory" element={<Inventory />} />
                <Route path="inventory/:id" element={<CustomerInventoryDetail />} />
                <Route path="inventory/:id/edit" element={<CustomerInventoryEdit />} />
                <Route path="requests" element={<RequestsList />} />
                <Route path="requests/new" element={<CreateRequest />} />
                <Route path="requests/:id/edit" element={<CreateRequest isEditing={true} />} />
                <Route path="requests/:id" element={<RequestDetail />} />
                <Route path="inbound-projects" element={<InboundProjects />} />
                <Route path="inbound-projects/new" element={<CreateInboundProject />} />
                <Route path="inbound-projects/:id" element={<InboundProjectDetail />} />
                <Route path="inbound-projects/:id/edit" element={<CreateInboundProject />} />
                <Route path="shipping/rates" element={<ShippingRates />} />
                <Route path="shipping/options" element={<ShippingOptions />} />
                <Route path="shipping/review" element={<ShippingReview />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="invoices/:id" element={<InvoiceDetail />} />
                <Route path="storage-fees" element={<CustomerStorageFees />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="company" element={<CustomerProfile />} />
                <Route path="user" element={<CustomerUserSettings />} />
                <Route path="feedback" element={<CustomerFeedback />} />
                <Route path="change-log" element={<ChangeLog scope="customer" />} />
                <Route path="help" element={<AdminHelp audience="customer" />} />
                <Route path="contracts" element={<Navigate to="/customer/company" replace />} />
                <Route path="profile" element={<Navigate to="/customer/user" replace />} />
                <Route path="addresses" element={<CustomerAddresses />} />
              </Route>

              {/* Admin routes */}
              <Route path="/admin" element={
                <AuthGuard requiredRole="admin">
                  <AdminLayout />
                </AuthGuard>
              }>
                <Route index element={<AdminDashboard />} />
                
                {/* Inventory */}
                <Route path="inventory" element={<InventoryList />} />
                <Route path="inventory/new" element={<InventoryForm />} />
                <Route path="inventory/:id" element={<AdminInventoryDetail />} />
                <Route path="inventory/:id/edit" element={<InventoryForm />} />
                
                {/* Customers */}
                <Route path="customers" element={<CustomersList />} />
                <Route path="customers/new" element={<CustomerForm />} />
                <Route path="customers/:id" element={<CustomerDetail />} />
                <Route path="customers/:id/edit" element={<CustomerForm />} />
                
                {/* Requests */}
                <Route path="requests" element={<AdminRequestsList />} />
                <Route path="requests/new" element={<AdminCreateRequest />} />
                <Route path="requests/:id" element={<AdminRequestDetail />} />
                <Route path="requests/:id/edit" element={<AdminCreateRequest isEditing={true} />} />
                <Route path="requests/shipping/options" element={<AdminShippingOptions />} />
                
                {/* Inbound Projects -- create form kept, list/detail removed (use requests view) */}
                <Route path="inbound-projects/new" element={<AdminCreateInboundProject />} />
                
                {/* Manifest Receiving */}
                <Route path="manifests/:id/receive" element={<ManifestReceivingScreen />} />
                
                {/* Receiving */}
                <Route path="receiving/:requestId" element={<ReceivingScreen />} />
                
                {/* Invoices */}
                <Route path="invoices" element={<InvoicesList />} />
                <Route path="invoices/:id" element={<AdminInvoiceDetail />} />
                
                {/* Users */}
                <Route path="users" element={<UsersList />} />
                <Route path="users/:id" element={<UserView />} />
                <Route path="users/:id/edit" element={<UserEdit />} />
                
                {/* Audit Logs */}
                <Route path="audit-logs" element={<AuditLogs />} />

                {/* Inventory Corrections */}
                <Route path="corrections" element={<CorrectionsList />} />
                <Route path="corrections/adjustments/new" element={<CreateAdjustment />} />
                <Route path="corrections/transfers/new" element={<CreateTransfer />} />
                <Route path="corrections/:id" element={<CorrectionDetail />} />
                {/* Billing exceptions (RF14 ops queue) */}
                <Route path="billing-exceptions" element={<BillingExceptions />} />
                {/* Storage units (RF2: physical class + billing override) */}
                <Route path="storage-units" element={<StorageUnitsList />} />
                <Route path="zoho-webhooks" element={<ZohoWebhookEvents />} />
                <Route path="help" element={<AdminHelp />} />
                
                {/* Settings */}
                <Route path="settings" element={<Settings />} />
                <Route path="change-log" element={<ChangeLog scope="admin" />} />
              </Route>

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            {/* Toast notifications container */}
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
            </Router>
          </PermissionProvider>
        </NotificationProvider>
    </QueryClientProvider>
  );
};

// Show landing page for visitors, redirect authenticated users to their dashboard
const RootRedirect = () => {
  const { isAuthenticated, userRole } = getAuth();

  if (isAuthenticated) {
    return <Navigate to={getDashboardUrl(userRole)} replace />;
  }

  return <LandingPage />;
};

export default App; 
